/**
 * Cancel a session, after showing what it would cost.
 *
 * *** THE SCREEN COMPUTES NOTHING ***
 * The cutoff, the refund rate, whether this role may cancel at all, and
 * whether a plan session comes back are ALL the client admin's settings,
 * resolved server-side by `/cancellation-preview`. A dialog that worked out
 * its own refund would eventually promise a number the server refuses —
 * which is exactly why that endpoint exists.
 *
 * *** AND IT ASKS BEFORE IT ACTS ***
 * The preview runs the same computation the cancellation itself performs,
 * so what is shown here is what will happen.
 */

import { useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { TextField } from '@/components/ui/Field'
import { useToast } from '@/components/ui/Toast'
import { ApiError } from '@/lib/api'
import { useCancelBooking, useCancellationPreview } from '@/lib/queries'
import { formatDateTime, formatMoney } from '@/lib/format'
import type { MyBooking } from '@/types/api'
import styles from './SessionsPage.module.css'

interface Props {
  booking: MyBooking
  onClose: () => void
}

export function CancelSessionDialog({ booking, onClose }: Props) {
  const toast = useToast()
  const [reason, setReason] = useState('')

  const { data: preview, isLoading, error } = useCancellationPreview(
    booking.booking_id,
  )
  const cancel = useCancelBooking()

  const confirm = async () => {
    try {
      await cancel.mutateAsync({
        bookingId: booking.booking_id,
        reason: reason.trim() || undefined,
      })
      toast.success('Session cancelled.')
      onClose()
    } catch (caught) {
      toast.error(
        caught instanceof ApiError
          ? caught.message
          : 'Could not cancel this session.',
      )
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Cancel this session?"
      description={formatDateTime(booking.slot_start_utc)}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={cancel.isPending}>
            Keep it
          </Button>
          <Button
            variant="danger"
            onClick={confirm}
            loading={cancel.isPending}
            /*
              Disabled until the server has said this is allowed. Enabling
              it while the preview loads would let someone cancel past the
              cutoff and be refused, having already been shown a dialog
              that implied they could.
            */
            disabled={!preview?.allowed || isLoading}
          >
            Cancel the session
          </Button>
        </>
      }
    >
      {isLoading && <p className={styles.muted}>Checking the policy…</p>}

      {error && (
        <p className={styles.error}>
          {error instanceof ApiError
            ? error.message
            : 'Could not check the cancellation policy.'}
        </p>
      )}

      {preview && !preview.allowed && (
        <p className={styles.error}>
          {/* The server's own sentence. It names which rule stopped it. */}
          {preview.reason ?? 'This session can no longer be cancelled.'}
        </p>
      )}

      {preview && preview.allowed && (
        <div className={styles.previewBody}>
          {/*
            *** A PLAN SESSION AND A PAID ONE END DIFFERENTLY ***
            One returns a session to the plan, the other refunds money.
            Saying "refund" on a plan booking would promise cash that is
            never coming.
          */}
          {preview.paid_with_plan ? (
            <p>
              {preview.session_returned
                ? 'This session will be returned to your plan.'
                : 'This session will NOT be returned to your plan.'}
            </p>
          ) : preview.refund_issuable ? (
            <p>
              You will be refunded{' '}
              <strong>
                {formatMoney(preview.refund_amount, preview.currency)}
              </strong>{' '}
              ({preview.refund_percent}%).
            </p>
          ) : (
            <p>No refund will be issued for this session.</p>
          )}

          {/*
            Why the number is what it is. Without the rule beside it, a
            partial refund reads as an error rather than a policy.
          */}
          <p className={styles.muted}>
            {preview.past_cutoff
              ? `The free-cancellation window closed ${preview.cutoff_hours} hours before the session.`
              : `Cancelling more than ${preview.cutoff_hours} hours ahead. This session starts in ${preview.hours_until_session} hours.`}
          </p>

          <TextField
            label="Reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={200}
            disabled={cancel.isPending}
            hint="Shared with the other person."
          />
        </div>
      )}
    </Modal>
  )
}
